import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  PrinterCheck,
  Printer,
  CheckCircle2,
  Loader2,
  Trash2,
  RotateCcw,
  FileText,
  Inbox,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_META = {
  fillable_form: { label: "Completed Form", cls: "bg-purple-50 text-purple-700 border-purple-200" },
  self_upload: { label: "Approved Upload", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  orientation_packet: { label: "Orientation Packet", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const notifyChanged = () => window.dispatchEvent(new Event("print-queue-changed"));

export default function PrintQueuePage() {
  const [status, setStatus] = useState("pending");
  const [jobs, setJobs] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(null); // "print-all" | "mark-all" | jobId
  const iframeRef = useRef(null);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/print-queue`, { params: { status } });
      setJobs(res.data.jobs || []);
      setPendingCount(res.data.pending_count || 0);
      setSelected(new Set());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load print queue");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const printBlobUrl = (url) =>
    new Promise((resolve) => {
      const iframe = iframeRef.current;
      if (!iframe) return resolve(false);
      iframe.onload = () => {
        setTimeout(() => {
          try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
            resolve(true);
          } catch {
            window.open(url, "_blank");
            resolve(false);
          }
        }, 400);
      };
      iframe.src = url;
    });

  const fetchAndPrint = async (url) => {
    const res = await axios.get(url, { responseType: "blob" });
    const blobUrl = URL.createObjectURL(res.data);
    await printBlobUrl(blobUrl);
  };

  const printJob = async (job) => {
    setBusy(job.id);
    try {
      await fetchAndPrint(`${API}/print-queue/${job.id}/pdf`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not open document");
    } finally {
      setBusy(null);
    }
  };

  const printMany = async (ids) => {
    setBusy("print-all");
    try {
      const qs = ids?.length ? `?ids=${ids.join(",")}` : "";
      await fetchAndPrint(`${API}/print-queue/merged.pdf${qs}`);
      toast.success(`Print job ready (${ids?.length || pendingCount} document${(ids?.length || pendingCount) === 1 ? "" : "s"})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not build print job");
    } finally {
      setBusy(null);
    }
  };

  const markPrinted = async (ids) => {
    setBusy(ids?.length === 1 ? ids[0] : "mark-all");
    try {
      const res = await axios.post(`${API}/print-queue/mark-printed`, { ids: ids?.length ? ids : null });
      toast.success(`${res.data.updated} document${res.data.updated === 1 ? "" : "s"} marked printed`);
      notifyChanged();
      fetchJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setBusy(null);
    }
  };

  const requeue = async (job) => {
    try {
      await axios.post(`${API}/print-queue/${job.id}/requeue`);
      toast.success("Sent back to the queue");
      notifyChanged();
      fetchJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Requeue failed");
    }
  };

  const removeJob = async (job) => {
    if (!window.confirm(`Remove "${job.filename}" from the print queue?`)) return;
    try {
      await axios.delete(`${API}/print-queue/${job.id}`);
      toast.success("Removed from queue");
      notifyChanged();
      fetchJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Remove failed");
    }
  };

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelected((prev) => (prev.size === jobs.length ? new Set() : new Set(jobs.map((j) => j.id))));

  const selectedIds = [...selected];
  const isPending = status === "pending";

  return (
    <Layout>
      <div className="space-y-6" data-testid="print-queue-page">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <PrinterCheck className="w-6 h-6 text-teal-600" />
              Office Print Queue
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Finalized employee documents land here automatically — approved forms, approved uploads, and orientation packets.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => printMany(selectedIds.length ? selectedIds : null)}
              disabled={!isPending || pendingCount === 0 || busy === "print-all"}
              className="gap-2 bg-teal-600 hover:bg-teal-700"
              data-testid="print-all-btn"
            >
              {busy === "print-all" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
              {selectedIds.length ? `Print Selected (${selectedIds.length})` : `Print All (${pendingCount})`}
            </Button>
            <Button
              variant="outline"
              onClick={() => markPrinted(selectedIds.length ? selectedIds : null)}
              disabled={!isPending || pendingCount === 0 || busy === "mark-all"}
              className="gap-2"
              data-testid="mark-all-printed-btn"
            >
              {busy === "mark-all" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {selectedIds.length ? "Mark Selected Printed" : "Mark All Printed"}
            </Button>
          </div>
        </div>

        <Tabs value={status} onValueChange={setStatus}>
          <TabsList>
            <TabsTrigger value="pending" data-testid="print-queue-tab-pending">
              Ready to Print
              {pendingCount > 0 && (
                <span className="ml-2 rounded-full bg-teal-600 text-white text-[10px] px-1.5 py-0.5 leading-none">
                  {pendingCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="printed" data-testid="print-queue-tab-printed">Printed</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-16 text-slate-500" data-testid="print-queue-empty">
              <Inbox className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="font-medium">{isPending ? "Nothing waiting to print" : "No printed history yet"}</p>
              <p className="text-sm mt-1">
                {isPending
                  ? "Approve an employee form or upload and it will show up here."
                  : "Documents you mark as printed will be listed here."}
              </p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                <tr>
                  {isPending && (
                    <th className="px-4 py-3 w-10">
                      <Checkbox
                        checked={selected.size === jobs.length && jobs.length > 0}
                        onCheckedChange={toggleAll}
                        data-testid="print-queue-select-all"
                        aria-label="Select all"
                      />
                    </th>
                  )}
                  <th className="text-left px-4 py-3 font-medium">Document</th>
                  <th className="text-left px-4 py-3 font-medium">Employee</th>
                  <th className="text-left px-4 py-3 font-medium">{isPending ? "Queued" : "Printed"}</th>
                  <th className="text-right px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {jobs.map((job) => {
                  const meta = KIND_META[job.kind] || { label: job.kind, cls: "bg-slate-50 text-slate-700" };
                  const rowBusy = busy === job.id;
                  return (
                    <tr key={job.id} className="hover:bg-slate-50/60" data-testid={`print-job-${job.id}`}>
                      {isPending && (
                        <td className="px-4 py-3">
                          <Checkbox
                            checked={selected.has(job.id)}
                            onCheckedChange={() => toggle(job.id)}
                            data-testid={`print-job-select-${job.id}`}
                            aria-label={`Select ${job.filename}`}
                          />
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                            <FileText className="w-4 h-4 text-slate-500" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 truncate" title={job.filename}>{job.filename}</p>
                            <Badge variant="outline" className={`text-[10px] mt-0.5 ${meta.cls}`}>{meta.label}</Badge>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{job.employee_name || "—"}</td>
                      <td className="px-4 py-3 text-slate-600 text-xs">
                        {isPending ? (
                          <>
                            {fmtDate(job.queued_at)}
                            {job.queued_by_name && <div className="text-slate-400">by {job.queued_by_name}</div>}
                          </>
                        ) : (
                          <>
                            {fmtDate(job.printed_at)}
                            {job.printed_by_name && <div className="text-slate-400">by {job.printed_by_name}</div>}
                          </>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => printJob(job)}
                            disabled={rowBusy}
                            className="gap-1"
                            data-testid={`print-job-print-${job.id}`}
                            title="Print"
                          >
                            {rowBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />}
                            Print
                          </Button>
                          {isPending ? (
                            <Button
                              size="sm"
                              onClick={() => markPrinted([job.id])}
                              disabled={rowBusy}
                              className="gap-1 bg-emerald-600 hover:bg-emerald-700"
                              data-testid={`print-job-done-${job.id}`}
                              title="Mark printed"
                            >
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              Done
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => requeue(job)}
                              className="gap-1"
                              data-testid={`print-job-requeue-${job.id}`}
                              title="Send back to queue"
                            >
                              <RotateCcw className="w-3.5 h-3.5" />
                              Requeue
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeJob(job)}
                            className="text-red-600 hover:bg-red-50"
                            data-testid={`print-job-remove-${job.id}`}
                            title="Remove"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <iframe
          ref={iframeRef}
          title="print-frame"
          style={{ position: "fixed", right: 0, bottom: 0, width: 0, height: 0, border: 0 }}
        />
      </div>
    </Layout>
  );
}
